import {
  alpha,
  Box,
  Typography,
  IconButton,
  useTheme,
  makeStyles,
  Theme,
  Chip,
  Grid,
  InputAdornment,
} from "@material-ui/core"
import { useElapsedMs } from "../hooks/use-hour-report-timer-elapsed"
import { Pause, Play, Square, RotateCcw, Clock } from "react-feather"
import { useHourReportTimer } from "../hooks/use-hour-report-timer"
import { useLocalizedFormat } from "../../hooks/use-localized-format"
import { useDateFormat } from "../../users/hooks/use-date-format"
import DatePicker from "../../components/DatePicker"
import { useTranslation } from "react-i18next"

const HourReportTimer = () => {
  const timer = useHourReportTimer()
  const { t } = useTranslation("hour-report")

  const timerStart = timer.state.startTime ? new Date(timer.state.startTime) : null
  const timerEnd = timer.state.endTime ? new Date(timer.state.endTime) : null
  const showInputs = timer.isIdle ? false : true

  const handleStartTime = (date: Date) => {
    date.setSeconds(0, 0)
    timer.updateStartTime(date.getTime())
  }

  const handleEndTime = (date: Date) => {
    date.setSeconds(0, 0)
    timer.updateEndTime(date.getTime())
  }

  return (
    <Box sx={{ py: 2, borderRadius: 8 }}>
      <TimerDisplay timer={timer} />
      <TimerActions timer={timer} />
      <Box mt={4} display={showInputs ? "block" : "none"}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={6}>
            <DatePicker
              onChange={(date) => date && handleStartTime(date)}
              selected={timerStart}
              dateFormat={"HH:mm"}
              placeholderText="HH:mm"
              timeIntervals={30}
              isClearable={false}
              textFieldProps={{
                label: t("form.startTime", { defaultValue: "Start Time" }),
                InputLabelProps: { shrink: true },
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Clock size={20} />
                    </InputAdornment>
                  ),
                },
              }}
              showTimeSelect
              showTimeSelectOnly
            />
          </Grid>
          {timer.isStopped ? (
            <Grid item xs={12} sm={6}>
              <DatePicker
                onChange={(date) => date && handleEndTime(date)}
                selected={timerEnd}
                dateFormat={"HH:mm"}
                placeholderText="HH:mm"
                timeIntervals={30}
                isClearable={false}
                textFieldProps={{
                  label: t("form.endTime", { defaultValue: "End Time" }),
                  InputLabelProps: { shrink: true },
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Clock size={20} />
                      </InputAdornment>
                    ),
                  },
                }}
                showTimeSelect
                showTimeSelectOnly
              />
            </Grid>
          ) : null}
        </Grid>
      </Box>
    </Box>
  )
}

export default HourReportTimer

const TimerDisplay = ({ timer }: { timer: Timer }) => {
  const { t } = useTranslation("hour-report")
  const { formattedElapsed } = useElapsedMs(timer.state)
  const theme = useTheme()
  const classes = useStyles()
  const localizedFormat = useLocalizedFormat()
  const { dateFormat } = useDateFormat()
  const dateTimeFormat = `${dateFormat} HH:mm`

  const chipColor = timer.isStopped
    ? theme.palette.error.main
    : timer.isPaused
    ? theme.palette.info.main
    : theme.palette.common.white
  const chipIcon = timer.isStopped ? <Square color={chipColor} /> : timer.isPaused ? <Pause color={chipColor} /> : null
  const chipLabel = timer.isPaused
    ? t("timer.pausedAt", {
        defaultValue: "Paused at {{dateTime}}",
        dateTime: localizedFormat(new Date(timer.state.currentPauseStart!), dateTimeFormat),
      })
    : timer.isStopped
    ? t("timer.stopped", { defaultValue: "Stopped" })
    : timer.state.status

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          my: timer.isPaused || timer.isStopped ? 2 : 0,
        }}
        style={{
          transition: "margin 0.3s ease-in-out",
        }}
      >
        <Chip
          className={classes.chip}
          label={chipLabel}
          size="small"
          variant="outlined"
          icon={chipIcon ?? undefined}
          style={{
            color: chipColor,
            borderColor: chipColor,
            opacity: timer.isStopped || timer.isPaused ? 1 : 0,
          }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", marginLeft: 12 }}>
        <Typography variant="h3">{formattedElapsed.HH}</Typography>
        <Typography variant="h3" style={{ paddingLeft: 8, paddingRight: 4 }}>
          :
        </Typography>
        <Typography variant="h3">{formattedElapsed.mm}</Typography>
        <Typography variant="h3" style={{ paddingLeft: 4, paddingRight: 4 }}>
          :
        </Typography>
        <Typography variant="h3">{formattedElapsed.ss}</Typography>
      </Box>
    </>
  )
}

const TimerActions = ({ timer }: { timer: Timer }) => {
  const classes = useStyles()
  const { t } = useTranslation("hour-report")

  return (
    <Box className={classes.timerActions} display="flex" alignItems="center" justifyContent={"center"} mt={2}>
      {timer.canStart || timer.canResume ? (
        <IconButton
          color="primary"
          onClick={timer.isPaused ? timer.resume : timer.start}
          aria-label={
            timer.isPaused
              ? t("timer.resume", { defaultValue: "Resume timer" })
              : t("timer.start", { defaultValue: "Start timer" })
          }
        >
          <Play size={24} />
        </IconButton>
      ) : null}
      {timer.canPause ? (
        <IconButton
          color="primary"
          onClick={timer.pause}
          aria-label={t("timer.pause", { defaultValue: "Pause timer" })}
        >
          <Pause size={24} />
        </IconButton>
      ) : null}
      {timer.canStop ? (
        <IconButton onClick={timer.stop} color="secondary" aria-label={t("timer.stop", { defaultValue: "Stop timer" })}>
          <Square size={24} />
        </IconButton>
      ) : null}
      {timer.canReset ? (
        <IconButton
          color="secondary"
          onClick={timer.reset}
          aria-label={t("timer.reset", { defaultValue: "Reset timer" })}
        >
          <RotateCcw size={24} />
        </IconButton>
      ) : null}
    </Box>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  timerActions: {
    "& .MuiIconButton-root": {
      borderRadius: theme.shape.borderRadius,
      border: "1px solid",
      margin: theme.spacing(0.5),
      transition: theme.transitions.create("margin"),
    },
    "& .MuiIconButton-colorPrimary": {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    "& .MuiIconButton-colorSecondary": {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      borderColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.text.primary,
    },
  },
  chip: {
    fontWeight: 600,
    transition: "opacity 0.3s ease-in-out",
    paddingLeft: 8,
    paddingRight: 8,
    "& .MuiChip-iconSmall": {
      width: 12,
      height: 12,
    },
  },
}))
type Timer = ReturnType<typeof useHourReportTimer>
